import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import { List, ListItem, Container, Section } from 'chaoskit/src/components'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'

import Image from '~components/Image'
import DemoQuote from '~helpers/demo-quote.json'
import Data from '~components/Data'
import StaticWrapper from '~components/StaticWrapper'
import Quote from '~components/Quote'
import QuoteImage from '~media/logos/grubhub-white.svg'
import { listBullet } from '~styles/utility'
import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import StaticFooter from '~partials/footer/StaticFooter'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import DemoForm from '~components/demo/DemoForm'

const DemoLandingPage = ({
  data: {
    mdx: { frontmatter: pageData },
  },
  pageContext: { nodeType },
}) => {
  const theme = useTheme()
  const newLayout = true

  return (
    <BaseLayout
      noFooter
      headerProps={{
        showPrimaryLogo: true,
        contrast: false,
        sticky: false,
        showNav: false,
      }}
    >
      <HelmetSEO
        title={pageData.seoTitle}
        description={pageData.description}
        noIndex
      />
      <NodeTypeTracker nodeType={nodeType} />
      <StaticWrapper css={{ paddingBottom: theme.space.large }}>
        <Container>
          <div
            css={{
              display: 'grid',
              gap: theme.space.large,

              [theme.mq.medium]: {
                gridTemplateColumns: '1fr 2px 1fr',
                gap: theme.space.xlarge,
              },
            }}
          >
            <div>
              <div>
                <div css={{ marginBottom: theme.space.large }}>
                  <h3>
                    <Data>{pageData.hero.heading}</Data>
                  </h3>
                </div>
                <div css={{ ...theme.text.large }}>
                  <Data>{pageData.blurb}</Data>
                </div>

                <div
                  css={{
                    color: theme.color.primary.base,
                    ...theme.headingPreset.h5,
                    marginTop: theme.space.base,
                  }}
                >
                  <Data>{pageData.list.heading}</Data>
                </div>

                <div css={{ ...theme.text.large }}>
                  <List space="base" css={[listBullet(theme)]}>
                    {pageData.list.items.map(({ blurb }) => (
                      <ListItem key={blurb}>
                        <Data>{blurb}</Data>
                      </ListItem>
                    ))}
                  </List>
                </div>
              </div>
            </div>
            <div
              css={{
                background: generateGradient({
                  start: theme.color.primary.base,
                  stop: theme.color.info.base,
                  position: 'to right',
                }),
                height: 2,
                display: 'block',

                [theme.mq.medium]: {
                  height: 'auto',
                  background: generateGradient({
                    start: theme.color.primary.base,
                    stop: theme.color.info.base,
                    position: 'to bottom',
                  }),
                },
              }}
            />
            <div>
              <div
                css={{
                  paddingBottom: theme.space.large,
                  [theme.mq.medium]: {
                    position: 'sticky',
                    top: theme.space.base,
                  },
                }}
              >
                <h3>
                  <Data>{pageData.form.heading}</Data>
                </h3>

                {pageData.form.image && (
                  <div
                    css={{
                      marginTop: theme.space.base * 2,
                      marginBottom: theme.space.base * 2,
                    }}
                  >
                    <Image src={pageData.form.image} alt="" />
                  </div>
                )}

                <DemoForm
                  id={pageData.form.embed}
                  fieldValues={{ programID: pageData.form.programId }}
                  thankYouMessage={pageData.form.thankYouMessage}
                />
              </div>
            </div>
          </div>
        </Container>
        <Section
          css={{ background: theme.brand.violet, textAlign: 'center' }}
          className="u-contrast"
        >
          <Container>
            <div
              css={{
                '.G__Quote__Author': {
                  color: `white !important`,
                  ...theme.text.large,
                },
                '.G__Quote__Title': {
                  ...theme.text.base,
                },
              }}
            >
              <Quote
                author={DemoQuote.author.name}
                title={DemoQuote.author.title}
                company={DemoQuote.company.name}
                logo={QuoteImage}
                newLayout={newLayout}
              >
                <Data>{DemoQuote.text}</Data>
              </Quote>
            </div>
          </Container>
        </Section>
      </StaticWrapper>
      <StaticFooter />
    </BaseLayout>
  )
}

DemoLandingPage.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

export default DemoLandingPage

export const pageQuery = graphql`
  query DemoLandingPageSingle($slug: String!) {
    mdx(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/demo-landing-pages/" }
    ) {
      fileAbsolutePath
      frontmatter {
        seoTitle
        description
        slug
        hero {
          heading
        }
        blurb
        list {
          heading
          items {
            blurb
          }
        }
        form {
          heading
          image
          embed
          programId
          thankYouMessage
        }
      }
    }
  }
`
